package com.swetdot.swetgram.data.realtime

import com.swetdot.swetgram.protocol.ProtocolJson
import com.swetdot.swetgram.protocol.dto.ReadReceiptEvent
import com.swetdot.swetgram.protocol.dto.RealtimeEvent
import com.swetdot.swetgram.protocol.dto.RealtimeEventTypes
import com.swetdot.swetgram.protocol.dto.TypingEvent
import kotlin.time.Instant
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject

/** One decoded line of Centrifugo's JSON protocol. */
internal sealed interface CentrifugoFrame {
    /**
     * An empty object. Centrifugo's application-level ping, which MUST be answered with another
     * empty object -- an unanswered one closes a socket that otherwise looks perfectly alive
     * about 25 seconds later, and the failure looks like a flaky network rather than a bug.
     */
    data object Ping : CentrifugoFrame

    data object ConnectAccepted : CentrifugoFrame

    /** Centrifugo answers a bad token with an error object, not by closing the connection. */
    data class ConnectRejected(val reply: String) : CentrifugoFrame

    data class Push(val event: RealtimeEvent) : CentrifugoFrame

    /**
     * Somebody started or stopped typing.
     *
     * Separate from [Push] because the reaction is the opposite one. A [Push] means "durable
     * state changed, go read REST"; this one carries the whole fact and must NOT cause a read.
     * Folding it into [Push] is not a tidiness question -- `ChatViewModel` answers every event
     * with `repository.refresh`, so it would produce a full message reload per typing burst in
     * every open conversation.
     */
    data class Typing(val event: TypingEvent) : CentrifugoFrame

    /**
     * Somebody's read watermark moved.
     *
     * Separate from [Push] for the same reason [Typing] is -- a [Push] means "go read REST", and
     * answering a receipt that way is the defect being fixed: a tick turning is not worth reloading
     * a conversation for. Folded into the timeline instead.
     *
     * Carries a parsed [Instant] rather than the DTO, unlike its two siblings. The wire value is a
     * string, and a string that will not parse is a watermark this client cannot compare against
     * anything -- so the decision has to be made once, here, where a bad frame becomes [Ignored]
     * and can be asserted. Handing an unparsed string upwards would push a `runCatching` into the
     * socket loop and leave "what if it is garbage" answered in two places.
     */
    data class ReadReceipt(
        val conversationId: String,
        val userId: String,
        val readUpTo: Instant,
    ) : CentrifugoFrame

    /** Anything this client has no use for: subscribe acks, unknown pushes, malformed lines. */
    data object Ignored : CentrifugoFrame
}

/**
 * Centrifugo's JSON protocol, reduced to the three things a Swetgram client needs: connect,
 * answer pings, read pushes.
 *
 * Kept as pure functions over strings so the frame shapes -- the part most likely to be wrong,
 * and the part a running server would only reveal at runtime -- are covered by ordinary tests.
 */
internal object CentrifugoProtocol {

    const val CONNECT_COMMAND_ID = 1

    /** The reply to [CentrifugoFrame.Ping]. Identical to the ping itself. */
    const val PONG = "{}"

    fun connectCommand(token: String): String =
        buildJsonObject {
            putJsonObject("connect") { put("token", JsonPrimitive(token)) }
            put("id", JsonPrimitive(CONNECT_COMMAND_ID))
        }.toString()

    fun parse(line: String): CentrifugoFrame {
        val message = runCatching { ProtocolJson.parseToJsonElement(line).jsonObject }.getOrNull()
            ?: return CentrifugoFrame.Ignored

        if (message.isEmpty()) return CentrifugoFrame.Ping

        if (message["id"]?.jsonPrimitive?.content?.toIntOrNull() == CONNECT_COMMAND_ID) {
            return if (message["error"] != null) {
                CentrifugoFrame.ConnectRejected(line)
            } else {
                CentrifugoFrame.ConnectAccepted
            }
        }

        val data = message["push"]?.jsonObject
            ?.get("pub")?.jsonObject
            ?.get("data") as? JsonObject ?: return CentrifugoFrame.Ignored

        // Dispatch on `type` BEFORE decoding: the two payloads are different shapes, and
        // `RealtimeEvent` would happily decode a typing frame while silently dropping the two
        // fields that carry its entire meaning -- leaving a frame that looks like a durable
        // change and gets answered with a REST read per keystroke.
        val type = data["type"]?.jsonPrimitive?.content

        if (type == RealtimeEventTypes.TYPING) {
            val typing = runCatching {
                ProtocolJson.decodeFromJsonElement(TypingEvent.serializer(), data)
            }.getOrNull() ?: return CentrifugoFrame.Ignored
            return CentrifugoFrame.Typing(typing)
        }

        // Dispatched here for the same reason typing is, and with the same consequence for getting
        // it wrong: `RealtimeEvent` decodes a receipt quite happily -- `type` and `conversation_id`
        // are both there -- and every consumer of a durable event answers with a full refetch. A
        // receipt read as a Push is a message reload per read call, which is a costlier version of
        // the very behaviour this frame was added to replace.
        if (type == RealtimeEventTypes.READ) {
            val receipt = runCatching {
                ProtocolJson.decodeFromJsonElement(ReadReceiptEvent.serializer(), data)
            }.getOrNull() ?: return CentrifugoFrame.Ignored
            val readUpTo = runCatching { Instant.parse(receipt.readUpToAt) }.getOrNull()
                ?: return CentrifugoFrame.Ignored
            return CentrifugoFrame.ReadReceipt(
                conversationId = receipt.conversationId,
                userId = receipt.userId,
                readUpTo = readUpTo,
            )
        }

        // An event kind added in a later server version must be ignored, not crash the socket:
        // decoding failure is Ignored rather than an exception for exactly that reason.
        val event = runCatching {
            ProtocolJson.decodeFromJsonElement(RealtimeEvent.serializer(), data)
        }.getOrNull() ?: return CentrifugoFrame.Ignored

        return CentrifugoFrame.Push(event)
    }
}
